// Bloc « Identité & NIP » de la fiche dossier (démarches exigeant une identité vérifiée seulement) : état du
// rattachement, vérification par NIP ou rapprochement sans NIP, procédure alternative, revérification et litiges.
// Le NIP saisi n'est ni affiché, ni conservé : le champ est vidé après l'envoi. Aucune réponse ne refuse la
// démarche ; la consigne du service guide l'agent. L'identité part de celle du demandeur du dossier (profil du
// compte ou nom du tiers) : un écart reste possible (acte, pièce), il est journalisé par le serveur.
import type { PreremplissageNip, ReferenceIdentiteNip, ResultatVerificationNip } from '@cc/contrats';
import { Bandeau, Bouton, CaseACocher, Champ, Panneau, Selection, Squelette, ZoneTexte } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { requete } from '../../../api/client';
import { ErreurApi } from '../../../api/erreurs';
import { dateHeure } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import styles from '../metier.module.css';

interface Demandeur {
  source: 'compte' | 'tiers';
  nom: string;
  prenoms: string | null;
  date_naissance: string | null;
}
type Etat = ReferenceIdentiteNip & { raccorde: boolean; requise: boolean; demandeur: Demandeur | null };

const LIBELLES_ETAT: Record<string, string> = {
  DECLAREE: 'Identité déclarée',
  PROPOSEE: 'Rapprochement proposé, à valider',
  EN_ATTENTE: 'En attente (titularité ou confirmation)',
  VERIFIEE: 'Identité vérifiée',
  LITIGIEUSE: 'Examen humain nécessaire',
  REVOQUEE: 'NIP révoqué',
  DECEDEE: 'Personne signalée décédée : examen par un agent',
  SANS_NIP: 'Sans NIP : procédure alternative au guichet',
};
const LIBELLES_ATTRIBUTS: Record<string, string> = { nom: 'Nom', prenoms: 'Prénoms', date_naissance: 'Date de naissance', lieu_naissance: 'Lieu de naissance', sexe: 'Sexe' };
const MOTIFS = [
  { valeur: 'erreur_etat_civil', libelle: 'Erreur d\'état civil' },
  { valeur: 'contestation', libelle: 'Contestation' },
  { valeur: 'doublon', libelle: 'Doublon possible' },
  { valeur: 'usurpation', libelle: 'Usurpation suspectée' },
  { valeur: 'autre', libelle: 'Autre' },
];

function useActionNip(dossierId: string, chemin: string, corps: unknown, succes: string, invalider: string[][]) {
  return useAction({ executer: () => requete(`/dossiers/${dossierId}/identite-nip/${chemin}`, { methode: 'POST', corps }), succes, invalider });
}

export function IdentiteNipDossier({ dossierId, peutVerifier, peutInstruire }: { dossierId: string; peutVerifier: boolean; peutInstruire: boolean }) {
  const cle = ['dossiers', 'identite-nip', dossierId];
  const etat = useQuery({ queryKey: cle, queryFn: ({ signal }) => requete<Etat>(`/dossiers/${dossierId}/identite-nip`, { signal }), retry: false });
  const [nip, setNip] = useState('');
  const [identite, setIdentite] = useState({ nom: '', prenoms: '', date_naissance: '' });
  const [titularite, setTitularite] = useState(false);
  const [acteAncien, setActeAncien] = useState(false);
  const [resultat, setResultat] = useState<ResultatVerificationNip | null>(null);
  const [preremplissage, setPreremplissage] = useState<PreremplissageNip | null>(null);
  const [litige, setLitige] = useState({ motif: 'erreur_etat_civil', observations: '' });
  const invalider = [cle, ['dossiers']];
  const demandeur = etat.data?.demandeur ?? null;
  const [prerempli, setPrerempli] = useState(false);
  // Préremplissage unique depuis le demandeur du dossier (mise à jour d'état pendant le rendu, sans effet).
  if (demandeur && !prerempli) {
    setPrerempli(true);
    setIdentite({ nom: demandeur.nom, prenoms: demandeur.prenoms ?? '', date_naissance: demandeur.date_naissance ?? '' });
  }
  const verification = useAction({
    executer: () =>
      requete<ResultatVerificationNip>(`/dossiers/${dossierId}/identite-nip/verifier`, {
        methode: 'POST',
        corps: { ...(nip.trim() ? { nip: nip.trim() } : {}), identite: { nom: identite.nom, prenoms: identite.prenoms, ...(identite.date_naissance ? { date_naissance: identite.date_naissance } : {}) }, titularite_controlee: titularite, acte_ancien: acteAncien },
      }),
    invalider,
    apres: (r) => {
      setNip('');
      setResultat(r);
      setPreremplissage(Object.keys(r.preremplissage).length ? r.preremplissage : null);
    },
  });
  const sansNip = useActionNip(dossierId, 'sans-nip', {}, 'Procédure alternative retenue.', invalider);
  const confirmer = useAction({
    executer: () => requete<ResultatVerificationNip>(`/dossiers/${dossierId}/identite-nip/confirmer-rapprochement`, { methode: 'POST', corps: { titularite_controlee: true } }),
    invalider,
    apres: (r) => setResultat(r),
  });
  const reverifier = useActionNip(dossierId, 'reverifier', {}, 'Revérification faite.', invalider);
  const signaler = useActionNip(dossierId, 'litige', litige, 'Litige signalé.', invalider);
  const lever = useActionNip(dossierId, 'litige/instruire', { issue: 'levee', observations: litige.observations || 'Contrôle effectué' }, 'Litige levé.', invalider);
  const maintenir = useActionNip(dossierId, 'litige/instruire', { issue: 'maintenue', observations: litige.observations || 'Litige confirmé' }, 'Litige maintenu.', invalider);

  if (etat.isPending) return <Panneau titre="Identité & NIP"><Squelette lignes={2} /></Panneau>;
  if (etat.error) return etat.error instanceof ErreurApi && etat.error.statut === 403 ? null : <Panneau titre="Identité & NIP"><Bandeau type="erreur">Impossible de lire l&apos;état de l&apos;identité.</Bandeau></Panneau>;
  const e = etat.data;
  if (!e.requise) return null;
  const erreur = [verification, sansNip, confirmer, reverifier, signaler, lever, maintenir].find((a) => a.messageErreur)?.messageErreur;
  return (
    <Panneau titre="Identité & NIP">
      <p>
        <strong>{LIBELLES_ETAT[e.etat] ?? e.etat}</strong>
        {e.verifiee_le ? `, le ${dateHeure(e.verifiee_le)}` : ''}
      </p>
      {e.motif ? <p className={styles.aide}>{e.motif}</p> : null}
      {e.ecarts.length ? <Bandeau type="attention">Écarts avec l&apos;identité présentée : {e.ecarts.map((x) => LIBELLES_ATTRIBUTS[x] ?? x).join(', ')}.</Bandeau> : null}
      {e.litige ? <p>Litige : {MOTIFS.find((m) => m.valeur === e.litige?.motif)?.libelle ?? e.litige.motif}{e.litige.issue ? ` (${e.litige.issue === 'levee' ? 'levé' : 'maintenu'})` : ' (ouvert)'}</p> : null}
      {!e.raccorde ? <Bandeau type="info">Le service Identité & NIP n&apos;est pas raccordé : vérification au guichet sur pièce.</Bandeau> : null}
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      {resultat ? <Bandeau type={resultat.statut === 'VERIFIED' ? 'succes' : 'attention'}>{resultat.consigne}</Bandeau> : null}
      {preremplissage ? (
        <div>
          <p>Informations vérifiées proposées (à reporter si elles concordent avec la pièce) :</p>
          <ul>
            {Object.entries(preremplissage).map(([k, v]) => (
              <li key={k}>{LIBELLES_ATTRIBUTS[k] ?? k} : {v}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {peutVerifier && e.raccorde ? (
        <form
          className={styles.pile}
          onSubmit={(ev) => {
            ev.preventDefault();
            verification.lancer(undefined);
          }}
        >
          <Champ id="nip-saisi" libelle="NIP présenté" aide="Facultatif : sans NIP, la date de naissance permet un rapprochement à valider. Le NIP n'est pas conservé." autoComplete="off" value={nip} onChange={(x) => setNip(x.target.value)} maxLength={64} facultatif />
          {demandeur ? <p className={styles.aide}>Identité reprise du {demandeur.source === 'compte' ? 'profil du demandeur' : 'nom du demandeur saisi au dépôt'} ; tout écart avec elle est journalisé.</p> : null}
          <Champ id="nip-nom" libelle="Nom" value={identite.nom} onChange={(x) => setIdentite({ ...identite, nom: x.target.value })} required />
          <Champ id="nip-prenoms" libelle="Prénoms" value={identite.prenoms} onChange={(x) => setIdentite({ ...identite, prenoms: x.target.value })} required />
          <Champ id="nip-naissance" libelle="Date de naissance" type="date" value={identite.date_naissance} onChange={(x) => setIdentite({ ...identite, date_naissance: x.target.value })} facultatif={Boolean(nip.trim())} />
          <CaseACocher libelle="Personne présente et pièce d'identité vue (titularité contrôlée)" checked={titularite} onChange={(x) => setTitularite(x.target.checked)} />
          <CaseACocher libelle="Acte de naissance ancien (écarts d'orthographe possibles)" checked={acteAncien} onChange={(x) => setActeAncien(x.target.checked)} />
          <div className={styles.pile}>
            <Bouton type="submit" variante="secondaire" chargement={verification.enCours} disabled={!identite.nom.trim() || !identite.prenoms.trim()}>
              {nip.trim() ? 'Vérifier le NIP' : 'Rechercher sans NIP'}
            </Bouton>
            <Bouton variante="discret" chargement={sansNip.enCours} onClick={() => sansNip.lancer(undefined)}>
              Pas de NIP : procédure alternative
            </Bouton>
            {e.etat === 'PROPOSEE' ? (
              <Bouton variante="secondaire" chargement={confirmer.enCours} onClick={() => confirmer.lancer(undefined)}>
                Valider le rapprochement (autre agent, titularité contrôlée)
              </Bouton>
            ) : null}
            {['VERIFIEE', 'EN_ATTENTE', 'LITIGIEUSE', 'REVOQUEE', 'DECEDEE'].includes(e.etat) ? (
              <Bouton variante="discret" chargement={reverifier.enCours} onClick={() => reverifier.lancer(undefined)}>
                Revérifier
              </Bouton>
            ) : null}
          </div>
        </form>
      ) : null}
      {peutVerifier && e.etat !== 'DECLAREE' && !(e.litige && !e.litige.issue) ? (
        <div className={styles.pile}>
          <Selection id="nip-motif-litige" libelle="Signaler un litige d'identité" options={MOTIFS} value={litige.motif} onChange={(x) => setLitige({ ...litige, motif: x.target.value })} />
          <ZoneTexte libelle="Observations" value={litige.observations} onChange={(x) => setLitige({ ...litige, observations: x.target.value })} aide="Erreur d'état civil : l'usager est orienté vers la mairie détentrice du registre ou ÉtatCivil Connect." />
          <Bouton variante="discret" chargement={signaler.enCours} disabled={litige.observations.trim().length < 3} onClick={() => signaler.lancer(undefined)}>
            Signaler le litige
          </Bouton>
        </div>
      ) : null}
      {peutInstruire && e.litige && !e.litige.issue ? (
        <div className={styles.pile}>
          <p className={styles.aide}>Instruction par un autre agent que l&apos;auteur du signalement.</p>
          <Bouton variante="secondaire" chargement={lever.enCours} onClick={() => lever.lancer(undefined)}>
            Lever le litige
          </Bouton>
          <Bouton variante="discret" chargement={maintenir.enCours} onClick={() => maintenir.lancer(undefined)}>
            Maintenir le litige
          </Bouton>
        </div>
      ) : null}
    </Panneau>
  );
}
